/**
 * Recently viewed products (localStorage).
 * Dedupes Combined Listing colorways via familyKey (one entry per model).
 */

/**
 * @typedef {{ id: string, familyKey: string }} RecentlyViewedEntry
 */

export class RecentlyViewed {
  /** @static @constant {string} */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} */
  static #MAX_PRODUCTS = 12;

  /**
   * @param {string | { id: string|number, familyKey?: string|number }} productIdOrEntry
   */
  static addProduct(productIdOrEntry) {
    const id =
      typeof productIdOrEntry === 'object' && productIdOrEntry
        ? String(productIdOrEntry.id || '')
        : String(productIdOrEntry || '');
    if (!id) return;

    const familyKey =
      typeof productIdOrEntry === 'object' && productIdOrEntry?.familyKey != null && productIdOrEntry.familyKey !== ''
        ? String(productIdOrEntry.familyKey)
        : id;

    let entries = this.getEntries().filter((entry) => entry.id !== id && entry.familyKey !== familyKey);
    entries.unshift({ id, familyKey });
    entries = entries.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(entries));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Product IDs only (compat predictive-search / empty state).
   * @returns {string[]}
   */
  static getProducts() {
    return this.getEntries().map((entry) => entry.id);
  }

  /**
   * @returns {RecentlyViewedEntry[]}
   */
  static getEntries() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      if (!Array.isArray(raw)) return [];

      /** @type {RecentlyViewedEntry[]} */
      const entries = [];
      const seenFamilies = new Set();

      for (const item of raw) {
        if (item == null) continue;

        /** @type {RecentlyViewedEntry} */
        let entry;
        if (typeof item === 'string' || typeof item === 'number') {
          entry = { id: String(item), familyKey: String(item) };
        } else if (typeof item === 'object' && item.id != null) {
          const id = String(item.id);
          entry = { id, familyKey: String(item.familyKey || id) };
        } else {
          continue;
        }

        if (!entry.id || seenFamilies.has(entry.familyKey)) continue;
        seenFamilies.add(entry.familyKey);
        entries.push(entry);
      }

      return entries;
    } catch {
      return [];
    }
  }
}
